/**
 * MotorBox Global JavaScript
 * Core functionality for the theme
 */

// Cart functionality
class Cart {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
  }

  bindEvents() {
    // Cart toggle
    const cartToggle = document.querySelector('[data-cart-toggle]');
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartClose = document.querySelector('[data-cart-close]');

    if (cartToggle && cartDrawer) {
      cartToggle.addEventListener('click', () => this.openCart());

      if (cartClose) {
        cartClose.addEventListener('click', () => this.closeCart());
      }

      // Close on backdrop click
      cartDrawer.addEventListener('click', (e) => {
        if (e.target === cartDrawer) {
          this.closeCart();
        }
      });
    }

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.updateCartCount();
      this.refreshCart();
    });
  }

  openCart() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      this.refreshCart();
    }
  }

  closeCart() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer) {
      cartDrawer.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCount = document.querySelectorAll('[data-cart-count]');

      cartCount.forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  async refreshCart() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (!cartDrawer) return;

    try {
      const response = await fetch('/cart?view=drawer');
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-items]');

      if (newContent) {
        const currentContent = cartDrawer.querySelector('[data-cart-items]');
        if (currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        }
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  async addItem(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add item to cart');
      }

      const item = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));
      this.openCart();

      return item;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async updateItem(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated'));

      return cart;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }
}

// Product functionality
class ProductForm {
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('[type="submit"]');
    this.init();
  }

  init() {
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.submitButton.hasAttribute('aria-disabled')) {
      return;
    }

    this.setLoading(true);

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || 1);

    try {
      await window.cart.addItem(variantId, quantity);
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error adding the product to your cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (loading) {
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.textContent = 'Adding...';
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.textContent = this.submitButton.dataset.originalText || 'Add to Cart';
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize cart
  window.cart = new Cart();

  // Initialize all product forms
  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    new ProductForm(form);
  });

  // Store original button text
  document.querySelectorAll('button[type="submit"]').forEach(button => {
    button.dataset.originalText = button.textContent;
  });
});

// Utility functions
const debounce = (fn, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

const throttle = (fn, delay = 300) => {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
};

// Lazy loading images
if ('IntersectionObserver' in window) {
  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        observer.unobserve(img);
      }
    });
  });

  document.querySelectorAll('img[data-src]').forEach(img => {
    imageObserver.observe(img);
  });
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { Cart, ProductForm, debounce, throttle };
}
